/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { ACTIVITY_EVENTS } from "components/jiffy/design-lab/shared/constants/index";
import { useCallback, useEffect, useRef } from "react";

type InactivityDetectorOptions = {
  interval: number;
  maxCallCount?: number;
  onInactive: () => void;
  onActive?: () => void;
  delayDetection?: number;
};

export function useUserInactivityDetector({
  interval,
  maxCallCount = Infinity,
  onInactive,
  onActive,
  delayDetection = 0
}: InactivityDetectorOptions) {
  const remainingCallsRef = useRef(maxCallCount);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDetectionStartedRef = useRef(false);
  const onInactiveRef = useRef(onInactive);
  const onActiveRef = useRef(onActive);
  const handleActivityRef = useRef<((event: Event) => void) | null>(null);

  onInactiveRef.current = onInactive;
  onActiveRef.current = onActive;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const schedule = useCallback(() => {
    if (!isDetectionStartedRef.current) return;
    clearTimer();
    if (remainingCallsRef.current <= 0) return;
    timerRef.current = setTimeout(() => {
      onInactiveRef.current?.();
      remainingCallsRef.current -= 1;
      if (remainingCallsRef.current > 0) {
        schedule();
      }
    }, interval);
  }, [interval, clearTimer]);

  const handleActivity = useCallback(
    (event: Event) => {
      const isTrusted = (event as any)?.isTrusted ?? true;
      if (!isTrusted) return;

      if (onActiveRef.current) onActiveRef.current();
      schedule();
    },
    [schedule]
  );

  handleActivityRef.current = handleActivity;

  const startDetection = useCallback(() => {
    if (isDetectionStartedRef.current) return;

    isDetectionStartedRef.current = true;
    schedule();

    ACTIVITY_EVENTS.forEach(name => {
      document.addEventListener(name, handleActivity, { passive: true });
    });
  }, [schedule, handleActivity]);

  const stopMonitoring = useCallback(() => {
    clearTimer();
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    isDetectionStartedRef.current = false;
    remainingCallsRef.current = maxCallCount;
    ACTIVITY_EVENTS.forEach(name => {
      document.removeEventListener(name, handleActivity);
    });
  }, [clearTimer, maxCallCount, handleActivity]);

  const resetTimer = useCallback(() => {
    if (isDetectionStartedRef.current) {
      clearTimer();
      remainingCallsRef.current = maxCallCount;
      schedule();
    }
  }, [clearTimer, maxCallCount, schedule]);

  const triggerUserInactivityMonitoring = useCallback(() => {
    if (!isDetectionStartedRef.current) {
      if (delayDetection > 0) {
        delayTimerRef.current = setTimeout(startDetection, delayDetection);
      } else {
        startDetection();
      }
    }
  }, [delayDetection, startDetection]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      clearTimer();
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      isDetectionStartedRef.current = false;
      ACTIVITY_EVENTS.forEach(name => {
        if (handleActivityRef.current) {
          document.removeEventListener(name, handleActivityRef.current);
        }
      });
    },
    [clearTimer]
  );

  return {
    triggerUserInactivityMonitoring,
    stopMonitoring,
    resetTimer
  };
}
